import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { IObject } from '../../../type_definitions/datatypes';
import * as _ from 'underscore';
import { GaEventService, IGAEvent } from '../services/ga-event.service';
import { ReportWrapperService } from './report-wrapper.service';
import { Subscription } from 'rxjs';
import { IColumnMetaData, IReportData, IReportMetadata, IReportResponse, ReportDataService } from './report-data.service';
import { ReportFiltersUtil } from './report-filters-util.service';
import { IBrowseListConfig } from '../browselists/browselists-config.service';
import { IEmbeddedReportGridReady } from './embedded-report/embedded-report.component';
import { util } from '../util';
import { GridOptions } from 'ag-grid-community';
import { ReportParameterCode, ReportParameterShortCode } from '@app/main/report/report-state.util';

export const EVENT_REPORT_WRAPPER_REFRESH:string = 'reportWrapper.refresh';

@Component({
  selector: 'report-wrapper',
  template: `
    <div class="report-wrapper">
      <h3 *ngIf="showTitle && title && !isInDialog">
        {{ title }}
        <span class="detailed-report-link">
          <a uiSref="main.reports" [uiParams]="reportRefParams" target="_blank">
            <i class="fa fa-table"></i>
          </a>
        </span>
      </h3>

      <embedded-report
        [reportId]="reportId"
        [browselistConfig]="browselistConfig"
        [browselistContext]="browselistContext"
        [reportMetadata]="reportMetadata"
        [reportDataLoading]="reportDataLoading"
        [reportData]="reportData"
        [queryFilters]="filters"
        [gridHeight]="gridHeight"
        [height]="height"
        [showActions]="showActions"
        [storedReport]="storedReport"
        [title]="title"
        [loadRowFn]="load"
        [showOperations]="showOperations"
        [templateCode]="templateCode"
        [gridOptions]="gridOptions"
        (onReportActionExecuted)="onActionExecuted.emit()"
        (onGridReady)="onGridReady($event)">
      </embedded-report>
    </div>
  `
})
export class ReportWrapperComponent implements OnChanges, OnInit, OnDestroy {
  @Input() public reportId:number;
  @Input() public browselistConfig:IBrowseListConfig;
  @Input() public browselistContext:IObject;
  @Input() public title:string;
  @Input() public showTitle:boolean = true;
  @Input() public filters:any;
  @Input() public gridHeight:string;
  @Input() public height:number;
  @Input() public visibleColumns:string[];
  @Input() public permittedActions:string[];
  @Input() public autoload:boolean;
  @Input() public storedReport:string;
  @Input() public isInDialog:boolean;
  @Input() public showActions:boolean;
  @Input() public showOperations:boolean;
  @Input() public templateCode:string;
  @Input() public gridOptions:GridOptions;
  @Output() public onActionExecuted:EventEmitter<void> = new EventEmitter<void>();

  public reportRefParams:{ [ key:string ]:any };
  public reportMetadata:IReportMetadata;
  public reportDataLoading:boolean;
  public reportData:any;

  private onEventSubscription:Subscription;
  private onReportLoadData:Subscription;

  constructor(
    private reportWrapperService:ReportWrapperService,
    private gaEventService:GaEventService,
    private reportFiltersUtil:ReportFiltersUtil,
    private reportDataService:ReportDataService,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    /**/
  }

  public ngOnInit():void {
    // register event listener
    this.listen();
  }

  public ngOnChanges(changesObj:SimpleChanges):void {
    if (changesObj.title) {
      this.title = changesObj.title.currentValue;
    }

    if (changesObj.showTitle) {
      this.showTitle = changesObj.showTitle.currentValue; // true by default
    }

    if (changesObj.storedReport) {
      this.storedReport = changesObj.storedReport.currentValue;
    }

    if (changesObj.filters && !_.isUndefined(changesObj.filters.currentValue)) {
      this.filters = changesObj.filters.currentValue;
    }

    if (changesObj.autoload) {
      this.autoload = changesObj.autoload.currentValue;
    }

    if (changesObj.isInDialog) {
      this.isInDialog = !!changesObj.isInDialog.currentValue;
    }

    if (changesObj.browselistConfig?.currentValue) {
      this.browselistConfig = changesObj.browselistConfig.currentValue;
      this.reportId = this.browselistConfig?.reportId;
      this.start();
    }

    if (changesObj.reportId?.currentValue) {
      this.reportId = changesObj.reportId.currentValue;
      this.start();
    }
  }

  public ngOnDestroy():void {
    this.onEventSubscription.unsubscribe();
    this.onReportLoadData.unsubscribe();
  }

  private start():void {
    // load metadata
    this.reportDataService.loadMetaData(this.reportId)
      .then((result:IReportMetadata) => {
        this.reportMetadata = this.checkVisibleColumns(result);
        this.reportMetadata = this.filterPermittedActions(this.reportMetadata);
        this.title = this.title || this.reportMetadata.title;
      });
  }

  private listen():void {
    this.onEventSubscription = this.gaEventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_REPORT_WRAPPER_REFRESH) {
        this.loadData();
      }
    });

    this.onReportLoadData = this.reportWrapperService.onLoadReportData().subscribe((reportId:number) => {
      if (reportId && reportId === this.reportId) {
        this.load();
      }
    });
  }

  public onGridReady(evt:IEmbeddedReportGridReady):void {
    this.filters = evt.filters;
    if (this.autoload || util.isNullOrUndefined(this.autoload)) { // this.autoload = false -> load report data at the later time
      this.load();
    }
  }

  // this method has to return a promise because is being used by the browselistService as 'loadAllRowsFn' to reload the rows
  public load = ():Promise<IReportData> => {
    this.reportRefParams = this.getReportRefParams();
    return this.loadData();
  };

  private loadData():Promise<IReportData> {
    // set default and null filters
    const queryFilters:any = this.reportFiltersUtil.getQueryFiltersFromModel(
      {},
      this.reportMetadata,
      {
        applyDefaultValues: true,
        applyNullValues: true
      }
    );

    // set report wrapper filters
    _.each(this.filters, (value:any, name:string) => {
      queryFilters[ name ] = value;
    });

    this.reportDataLoading = true;
    return this.reportDataService.loadData(`report ${this.reportId || ''}`, this.reportId, queryFilters, {
      average: false,
      min: false,
      max: false,
      count: false,
      sum: false
    }, this.reportMetadata.columns)
      .then((reportData:IReportResponse) => {
        this.reportData = reportData.data;
        this.changeDetectorRef.detectChanges();
        return this.reportData;
      })
      .finally(() => {
        this.reportDataLoading = false;
      });
  }

  private getReportRefParams():IObject {
    const queryFilters:IObject = this.reportFiltersUtil.getQueryFiltersFromModel(
      {},
      this.reportMetadata,
      {
        applyDefaultValues: true,
        applyNullValues: false
      }
    );

    _.each(this.filters, (value:any, name:string) => {
      queryFilters[ name ] = value;
    });

    return {
      [ReportParameterCode.REPORT_ID]: this.reportId,
      [ReportParameterShortCode.AUTO_LOAD]: true,
      [ReportParameterShortCode.FILTERS]: this.reportFiltersUtil.filtersToParam(queryFilters)
    };
  }

  private checkVisibleColumns(metadata:IReportMetadata):IReportMetadata {
    if (this.visibleColumns && this.visibleColumns.length) {
      _.forEach(metadata.columns, (col:IColumnMetaData) => {
        col.formatting.defaultinlist = this.visibleColumns.indexOf(col.name) > -1;
        return col;
      });
    }
    return metadata;
  }

  private filterPermittedActions(metadata:IReportMetadata):IReportMetadata {
    if (this.permittedActions && this.permittedActions.length) {
      metadata.actions = metadata.actions.filter(action => this.permittedActions.indexOf(action.code) > -1);
    }
    return metadata;
  }
}